import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";

export default function TaskForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    title: params.get("title") || "",
    description: "",
    deadline: "",
    employee_id: "",
  });
  const orderId = params.get("order_id") || null;
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Task title is required"); return; }
    setSaving(true);
    try {
      await apiClient.post("/tasks", {
        title: form.title.trim(),
        description: form.description.trim(),
        deadline: form.deadline || null,
        employee_id: form.employee_id || null,
        order_id: orderId,
      });
      toast.success("Task assigned");
      navigate("/tasks");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <>
      <AppHeader title="New Task" subtitle="Assign work to staff" />
      <div className="px-4 pt-4 pb-28">
        <button onClick={() => navigate(-1)} data-testid="task-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <form onSubmit={onSubmit} data-testid="task-form" className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <div className="space-y-1.5">
            <Label>Title *</Label>
            <Input data-testid="task-input-title" value={form.title} onChange={set("title")} placeholder="e.g., Pick up rice from supplier" required className="h-14 rounded-xl text-base" />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea data-testid="task-input-desc" value={form.description} onChange={set("description")} rows={3} placeholder="Details" className="rounded-xl text-base" />
          </div>
          <div className="space-y-1.5">
            <Label>Deadline</Label>
            <Input data-testid="task-input-deadline" type="date" value={form.deadline} onChange={set("deadline")} className="h-14 rounded-xl text-base" />
          </div>
          <div className="space-y-1.5">
            <Label>Assign to</Label>
            {employees.length === 0 ? (
              <Button type="button" onClick={() => navigate("/employees")} variant="outline" className="w-full h-14 rounded-xl">
                Add an employee first
              </Button>
            ) : (
              <Select value={form.employee_id || "none"} onValueChange={(v) => set("employee_id")(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="task-employee-select" className="h-14 rounded-xl text-base"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.role}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <Button type="submit" disabled={saving} data-testid="task-save-btn" className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-lg font-semibold gap-2">
            <Save className="w-5 h-5" /> {saving ? "Saving..." : "Create Task"}
          </Button>
        </form>
      </div>
    </>
  );
}
